import {
  AddressSchema,
  EmailSchema,
  isFinancialAidStaffCorrectionProtectedPath,
  PhoneNumberSchema,
} from '@triedge/financial-aid-domain';
import { resolveFinancialAidDisplayYears } from '../../../financial-aid/financialAidAcademicYear';

export const readCorrectionExpectedUpdatedAt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

type CorrectionFieldType = 'text' | 'number' | 'boolean' | 'empty';

export type FinancialAidCorrectionField = {
  path: string;
  label: string;
  group: string;
  type: CorrectionFieldType;
  value: unknown;
  inputValue: string;
};

export type FinancialAidCorrectionPatch = {
  sectionKey: string;
  patch: unknown;
};

export const formatFinancialAidCorrectionFieldOptionLabel = (field: FinancialAidCorrectionField): string =>
  field.group ? `${field.group} - ${field.label}` : field.label;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

export const isFinancialAidCorrectionProtectedPath = isFinancialAidStaffCorrectionProtectedPath;

const PARENT_SNAPSHOT_PREFIX = 'additionalInfo.parentCollectedFinancialAidSnapshot';
const PARENT_SNAPSHOT_PREFIX_SEGMENTS = PARENT_SNAPSHOT_PREFIX.split('.');

const readFieldType = (value: unknown): CorrectionFieldType => {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (value == null) return 'empty';
  return 'text';
};

export const readFinancialAidCorrectionInputValue = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

type FlattenFinancialAidCorrectionOptions = {
  academicYear?: unknown;
};

const humanizeCorrectionSegment = (segment: string): string => {
  if (/^\d+$/.test(segment)) {
    return `#${Number(segment) + 1}`;
  }
  return segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const resolveParentSnapshotCorrectionGroup = (
  topLevelKey: string,
  options: FlattenFinancialAidCorrectionOptions,
): string => {
  const displayYears = resolveFinancialAidDisplayYears(options.academicYear);
  const estimateYear = displayYears.estimateYear ? ` (${displayYears.estimateYear})` : '';
  const currentCampYear = displayYears.currentCampYear ? ` (${displayYears.currentCampYear})` : '';
  const priorCampYear = displayYears.priorCampYear ? ` (${displayYears.priorCampYear})` : '';
  switch (topLevelKey) {
    case 'householdComposition':
      return 'Household';
    case 'father':
      return 'Father / Guardian 1';
    case 'mother':
      return 'Mother / Guardian 2';
    case 'incomeFather':
      return 'Father income';
    case 'incomeMother':
      return 'Mother income';
    case 'benefits':
      return `Benefits and additional income${estimateYear}`;
    case 'assets':
      return 'Residence and assets';
    case 'investments':
      return 'Investments';
    case 'childrenAssets':
      return 'Children assets';
    case 'savings':
      return 'Savings';
    case 'checking':
      return 'Checking';
    case 'expenses':
      return 'Monthly expenses';
    case 'otherChildren':
      return 'Other children tuition';
    case 'majorExp':
      return 'Major expenses';
    case 'camps2025':
      return `Summer camps${currentCampYear}`;
    case 'camps2024':
      return `Prior-year summer camps${priorCampYear}`;
    case 'contract':
      return 'Tuition contract';
    case 'request':
      return 'Aid request';
    case 'vacation':
      return 'Vacation';
    default:
      return humanizeCorrectionSegment(topLevelKey);
  }
};

const formatIncomeCorrectionLabel = (
  fieldKey: string,
  options: FlattenFinancialAidCorrectionOptions,
): string | null => {
  const displayYears = resolveFinancialAidDisplayYears(options.academicYear);
  const actualYear = displayYears.actualYear ? ` (${displayYears.actualYear})` : '';
  const estimateYear = displayYears.estimateYear ? ` (${displayYears.estimateYear})` : '';
  switch (fieldKey) {
    case 'gross2024':
      return `Gross income${actualYear}`;
    case 'parsonage':
      return `Parsonage${actualYear}`;
    case 'fedTax':
      return `Federal tax${actualYear}`;
    case 'stateTax':
      return `State tax${actualYear}`;
    case 'ficaTax':
      return `FICA tax${actualYear}`;
    case 'refunds':
      return `Tax refunds / credits${actualYear}`;
    case 'estimated2025':
      return `Estimated income${estimateYear}`;
    default:
      return null;
  }
};

const formatParentSnapshotFieldLabel = (
  topLevelKey: string,
  fieldSegments: string[],
  options: FlattenFinancialAidCorrectionOptions,
): string => {
  if ((topLevelKey === 'incomeFather' || topLevelKey === 'incomeMother') && fieldSegments.length === 1) {
    return formatIncomeCorrectionLabel(fieldSegments[0] ?? '', options)
      ?? humanizeCorrectionSegment(fieldSegments[0] ?? topLevelKey);
  }
  return fieldSegments.length > 0
    ? fieldSegments.map(humanizeCorrectionSegment).join(' / ')
    : humanizeCorrectionSegment(topLevelKey);
};

const formatParentSnapshotCorrectionLabel = (
  path: string,
  options: FlattenFinancialAidCorrectionOptions,
): { label: string; group: string } => {
  const segments = path.split('.').slice(PARENT_SNAPSHOT_PREFIX_SEGMENTS.length);
  const [topLevelKey, ...fieldSegments] = segments;
  const group = resolveParentSnapshotCorrectionGroup(topLevelKey ?? 'fields', options);
  const label = formatParentSnapshotFieldLabel(topLevelKey ?? path, fieldSegments, options);
  return { label, group };
};

const isParentSnapshotCorrectionPath = (path: string): boolean =>
  path === PARENT_SNAPSHOT_PREFIX || path.startsWith(`${PARENT_SNAPSHOT_PREFIX}.`);

const isEditableParentSnapshotCorrectionPath = (path: string): boolean => {
  if (!isParentSnapshotCorrectionPath(path)) {
    return false;
  }
  if (
    path === `${PARENT_SNAPSHOT_PREFIX}.version`
    || path.endsWith('.id')
    || path === `${PARENT_SNAPSHOT_PREFIX}.signatures`
    || path.startsWith(`${PARENT_SNAPSHOT_PREFIX}.signatures.`)
  ) {
    return false;
  }
  return !isFinancialAidCorrectionProtectedPath(path);
};

const KNOWN_PARENT_SNAPSHOT_ARRAY_ROW_FIELDS: Record<string, readonly string[]> = {
  vehicles: ['year', 'make', 'model', 'value', 'debt', 'payment'],
  investments: ['description', 'value'],
  childrenAssets: ['description', 'value'],
  otherChildren: ['name', 'age', 'grade', 'school', 'fullTuition', 'scholarship'],
  camps2025: ['childName', 'campName', 'fee'],
  camps2024: ['childName', 'campName', 'fee'],
};

const KNOWN_PARENT_SNAPSHOT_BOOLEAN_FIELDS = new Set([
  'benefits.gifts',
  'father.isUnemployed',
  'father.isDivorced',
  'mother.isUnemployed',
  'mother.isDivorced',
  'expenses.taxesIncluded',
  'majorExp.servicesToYST',
  'vacation.traveled',
  'vacation.parentsTraveled',
  'vacation.childrenTraveled',
]);

const readParentSnapshotRelativePath = (path: string): string => (
  path.startsWith(`${PARENT_SNAPSHOT_PREFIX}.`)
    ? path.slice(PARENT_SNAPSHOT_PREFIX.length + 1)
    : ''
);

const hasSubmittedCorrectionValue = (value: unknown): boolean => {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'boolean') {
    return value;
  }
  return true;
};

const isBlankParentSnapshotArrayRow = (arrayKey: string, value: unknown): boolean => {
  const fieldKeys = KNOWN_PARENT_SNAPSHOT_ARRAY_ROW_FIELDS[arrayKey];
  if (!fieldKeys || !isRecord(value)) {
    return false;
  }
  return fieldKeys.every((fieldKey) => !hasSubmittedCorrectionValue(value[fieldKey]));
};

const isKnownParentSnapshotBooleanPath = (path: string): boolean =>
  KNOWN_PARENT_SNAPSHOT_BOOLEAN_FIELDS.has(readParentSnapshotRelativePath(path));

const KNOWN_PARENT_SNAPSHOT_NUMERIC_FIELDS = new Set([
  'father.unemploymentWeeklyAmount',
  'mother.unemploymentWeeklyAmount',
  'incomeFather.gross2024',
  'incomeFather.parsonage',
  'incomeFather.fedTax',
  'incomeFather.stateTax',
  'incomeFather.ficaTax',
  'incomeFather.refunds',
  'incomeFather.estimated2025',
  'incomeMother.gross2024',
  'incomeMother.parsonage',
  'incomeMother.fedTax',
  'incomeMother.stateTax',
  'incomeMother.ficaTax',
  'incomeMother.refunds',
  'incomeMother.estimated2025',
  'benefits.healthInsurance',
  'benefits.tuition',
  'benefits.auto',
  'benefits.other',
  'benefits.giftsAmount',
  'assets.purchasePrice',
  'assets.mortgageBalance',
  'assets.mortgagePayment',
  'assets.mortgageYears',
  'assets.propertyTaxes',
  'assets.interestRate',
  'assets.rentPayment',
  'assets.utilities',
  'savings.balance',
  'checking.balance',
  'expenses.housing',
  'expenses.utilities',
  'expenses.studentLoans',
  'expenses.food',
  'expenses.tuition',
  'expenses.bankLoans',
  'expenses.lifeInsurance',
  'expenses.shulDues',
  'expenses.carInsurance',
  'expenses.healthInsurance',
  'expenses.cleaningHelp',
  'expenses.other',
  'majorExp.homeImprovements',
  'majorExp.largePurchases',
  'majorExp.simchaCost',
  'majorExp.loanAmount',
  'contract.childrenCount',
  'contract.totalTuition',
  'contract.totalFees',
  'contract.preschoolTuition',
  'contract.eligibleTuition',
  'request.requestedAmount',
  'request.offeredTuition',
  'request.offeredFees',
  'vacation.cost',
]);

const KNOWN_PARENT_SNAPSHOT_NUMERIC_ARRAY_FIELDS: Record<string, readonly string[]> = {
  vehicles: ['year', 'value', 'debt', 'payment'],
  investments: ['value'],
  childrenAssets: ['value'],
  otherChildren: ['fullTuition', 'scholarship'],
  camps2025: ['fee'],
  camps2024: ['fee'],
};

const isKnownParentSnapshotNumericPath = (path: string): boolean => {
  const relativePath = readParentSnapshotRelativePath(path);
  if (KNOWN_PARENT_SNAPSHOT_NUMERIC_FIELDS.has(relativePath)) {
    return true;
  }
  const [arrayKey, index, fieldKey] = relativePath.split('.');
  return Boolean(
    arrayKey
    && /^\d+$/.test(index ?? '')
    && fieldKey
    && KNOWN_PARENT_SNAPSHOT_NUMERIC_ARRAY_FIELDS[arrayKey]?.includes(fieldKey)
  );
};

const readCorrectionFieldTypeForPath = (path: string, value: unknown): CorrectionFieldType =>
  isKnownParentSnapshotBooleanPath(path)
    ? 'boolean'
    : isKnownParentSnapshotNumericPath(path)
      ? 'number'
      : readFieldType(value);

const normalizeNumberInput = (rawValue: string): number | null => {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return null;
  }
  const next = Number(trimmed);
  if (!Number.isFinite(next)) {
    throw new Error('Corrected value must be a finite number.');
  }
  return next;
};

const assertValidParentContactCorrectionValue = (path: string, rawValue: string): void => {
  const relativePath = readParentSnapshotRelativePath(path);
  const value = rawValue.trim();
  if (/^(father|mother)\.email$/.test(relativePath) && !EmailSchema.safeParse(value).success) {
    throw new Error('Corrected email must be a valid email address.');
  }
  if (/^(father|mother)\.(cellPhone|homePhone)$/.test(relativePath) && !PhoneNumberSchema.safeParse(value).success) {
    throw new Error('Corrected phone must be a valid phone number.');
  }
  if (
    /^(father|mother)\.zip$/.test(relativePath)
    && !AddressSchema.safeParse({
      street: 'x',
      city: 'x',
      state: 'x',
      zipCode: value,
    }).success
  ) {
    throw new Error('Corrected ZIP must be a valid ZIP code.');
  }
};

const shouldTrimParentContactCorrectionValue = (path: string): boolean => {
  const relativePath = readParentSnapshotRelativePath(path);
  return /^(father|mother)\.(email|cellPhone|homePhone|zip)$/.test(relativePath);
};

const KNOWN_PARENT_PERSONAL_FIELDS = [
  'title',
  'firstName',
  'lastName',
  'address',
  'city',
  'state',
  'zip',
  'cellPhone',
  'homePhone',
  'email',
  'occupation',
  'employer',
  'businessName',
  'shul',
  'rabbi',
  'isUnemployed',
  'isDivorced',
  'unemploymentWeeklyAmount',
] as const;

const KNOWN_INCOME_FIELDS = [
  'gross2024',
  'parsonage',
  'fedTax',
  'stateTax',
  'ficaTax',
  'refunds',
  'estimated2025',
] as const;

const KNOWN_PARENT_SNAPSHOT_RECORD_FIELDS: Record<string, readonly string[]> = {
  householdComposition: ['mode', 'primaryKey', 'primaryRole', 'otherRoleLabel'],
  father: KNOWN_PARENT_PERSONAL_FIELDS,
  mother: KNOWN_PARENT_PERSONAL_FIELDS,
  incomeFather: KNOWN_INCOME_FIELDS,
  incomeMother: KNOWN_INCOME_FIELDS,
  benefits: ['healthInsurance', 'tuition', 'auto', 'other', 'gifts', 'giftsAmount'],
  assets: [
    'housingType',
    'purchasePrice',
    'purchaseDate',
    'mortgageBalance',
    'mortgagePayment',
    'mortgageYears',
    'propertyTaxes',
    'interestRate',
    'rentPayment',
    'utilities',
    'additionalProperties',
  ],
  savings: ['bank', 'accountNumber', 'balance'],
  checking: ['bank', 'accountNumber', 'balance'],
  expenses: [
    'housing',
    'taxesIncluded',
    'utilities',
    'studentLoans',
    'food',
    'tuition',
    'bankLoans',
    'lifeInsurance',
    'shulDues',
    'carInsurance',
    'healthInsurance',
    'cleaningHelp',
    'other',
    'medicalCoverage',
    'medicalCoverageDetails',
  ],
  majorExp: [
    'homeImprovements',
    'homeImprovementsDetails',
    'largePurchases',
    'largePurchasesDetails',
    'extraordinaryObligations',
    'simchaType',
    'simchaCost',
    'servicesToYST',
    'servicesDescription',
    'loanAmount',
  ],
  contract: ['childrenCount', 'totalTuition', 'totalFees', 'preschoolTuition', 'eligibleTuition'],
  request: ['requestedAmount', 'offeredTuition', 'offeredFees'],
  vacation: ['traveled', 'destinations', 'cost', 'parentsTraveled', 'childrenTraveled'],
};

const PARENT_SNAPSHOT_RECORD_KEYS = ['father', 'mother'] as const;
type ParentSnapshotRecordKey = typeof PARENT_SNAPSHOT_RECORD_KEYS[number];

const hasKnownParentSlot = (
  sections: Record<string, unknown>,
  snapshot: Record<string, unknown>,
  parentKey: ParentSnapshotRecordKey,
): boolean => {
  if (isRecord(snapshot[parentKey])) {
    return true;
  }
  const householdComposition = snapshot.householdComposition;
  if (isRecord(householdComposition)) {
    const mode = String(householdComposition.mode ?? '');
    const primaryKey = String(householdComposition.primaryKey ?? '');
    if (primaryKey === parentKey || mode === 'two_parent') {
      return true;
    }
  }
  return parentKey === 'father'
    ? isRecord(sections.parent1)
    : isRecord(sections.parent2);
};

const readCanonicalVehicleSnapshots = (sections: Record<string, unknown>): unknown[] => {
  const vehiclesSection = isRecord(sections.vehicles) ? sections.vehicles : {};
  const vehicles = Array.isArray(vehiclesSection.vehicles) ? vehiclesSection.vehicles : [];
  return vehicles
    .filter(isRecord)
    .map((vehicle) => ({
      year: vehicle.year ?? '',
      make: vehicle.make ?? '',
      model: vehicle.model ?? '',
      value: vehicle.estimatedValue ?? '',
      debt: vehicle.loanBalance ?? '',
      payment: vehicle.monthlyPayment ?? '',
    }));
};

const seedKnownParentSnapshotRecords = (
  sections: Record<string, unknown>,
  snapshot: unknown,
): unknown => {
  if (!isRecord(snapshot)) {
    return snapshot;
  }
  const seeded: Record<string, unknown> = { ...snapshot };
  PARENT_SNAPSHOT_RECORD_KEYS.forEach((parentKey) => {
    if (!isRecord(seeded[parentKey]) && hasKnownParentSlot(sections, snapshot, parentKey)) {
      seeded[parentKey] = {};
    }
  });
  if (!Array.isArray(seeded.vehicles)) {
    const vehicles = readCanonicalVehicleSnapshots(sections);
    if (vehicles.length > 0) {
      seeded.vehicles = vehicles;
    }
  }
  return seeded;
};

const listParentSnapshotRecordKeys = (value: Record<string, unknown>, path: string): string[] => {
  const relativePath = path.startsWith(`${PARENT_SNAPSHOT_PREFIX}.`)
    ? path.slice(PARENT_SNAPSHOT_PREFIX.length + 1)
    : '';
  const [arrayKey, index, extra] = relativePath.split('.');
  if (!extra && index != null && /^\d+$/.test(index)) {
    return [...(KNOWN_PARENT_SNAPSHOT_ARRAY_ROW_FIELDS[arrayKey] ?? Object.keys(value))];
  }
  const knownKeys = KNOWN_PARENT_SNAPSHOT_RECORD_FIELDS[relativePath];
  return knownKeys ? [...knownKeys] : Object.keys(value);
};

const isKnownParentSnapshotCorrectionLeafPath = (path: string): boolean => {
  const relativePath = readParentSnapshotRelativePath(path);
  if (!relativePath) {
    return false;
  }
  const [topLevelKey, secondSegment, thirdSegment, ...restSegments] = relativePath.split('.');
  if (topLevelKey && /^\d+$/.test(secondSegment ?? '') && thirdSegment && restSegments.length === 0) {
    return Boolean(KNOWN_PARENT_SNAPSHOT_ARRAY_ROW_FIELDS[topLevelKey]?.includes(thirdSegment));
  }
  if (topLevelKey && secondSegment && !thirdSegment) {
    return Boolean(KNOWN_PARENT_SNAPSHOT_RECORD_FIELDS[topLevelKey]?.includes(secondSegment));
  }
  return false;
};

const flattenParentSnapshotCorrectionFields = (
  value: unknown,
  path: string,
  fields: FinancialAidCorrectionField[],
  options: FlattenFinancialAidCorrectionOptions,
): void => {
  if (!isEditableParentSnapshotCorrectionPath(path)) {
    return;
  }
  if (Array.isArray(value)) {
    const arrayKey = readParentSnapshotRelativePath(path);
    value.forEach((item, index) => {
      if (isBlankParentSnapshotArrayRow(arrayKey, item)) {
        return;
      }
      flattenParentSnapshotCorrectionFields(item, `${path}.${index}`, fields, options);
    });
    return;
  }
  if (isRecord(value)) {
    listParentSnapshotRecordKeys(value, path).forEach((key) =>
      flattenParentSnapshotCorrectionFields(value[key] ?? null, `${path}.${key}`, fields, options)
    );
    return;
  }
  if (value === undefined || (value === null && !path.includes('.'))) {
    return;
  }
  if (isParentSnapshotCorrectionPath(path) && !isKnownParentSnapshotCorrectionLeafPath(path)) {
    return;
  }
  const { label, group } = formatParentSnapshotCorrectionLabel(path, options);
  const type = readCorrectionFieldTypeForPath(path, value);
  fields.push({
    path,
    label,
    group,
    type,
    value,
    inputValue: type === 'boolean' && value == null
      ? ''
      : readFinancialAidCorrectionInputValue(value),
  });
};

export const flattenFinancialAidCorrectionFields = (
  sections: unknown,
  options: FlattenFinancialAidCorrectionOptions = {},
): FinancialAidCorrectionField[] => {
  if (!isRecord(sections)) {
    return [];
  }
  const fields: FinancialAidCorrectionField[] = [];
  const snapshot = readFinancialAidCorrectionPathValue(sections, PARENT_SNAPSHOT_PREFIX);
  flattenParentSnapshotCorrectionFields(
    seedKnownParentSnapshotRecords(sections, snapshot),
    PARENT_SNAPSHOT_PREFIX,
    fields,
    options,
  );
  return fields.sort((left, right) => left.path.localeCompare(right.path));
};

const normalizeCorrectionSearchText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const filterFinancialAidCorrectionFields = (
  fields: FinancialAidCorrectionField[],
  query: string,
): FinancialAidCorrectionField[] => {
  const normalizedQuery = normalizeCorrectionSearchText(query);
  if (!normalizedQuery) {
    return fields;
  }
  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  return fields.filter((field) => {
    const haystack = normalizeCorrectionSearchText(`${field.path} ${field.group} ${field.label}`);
    return queryTokens.every((token) => haystack.includes(token));
  });
};

export type FinancialAidCorrectionFieldSelection = {
  path: string;
  inputValue: string;
};

export const resolveFinancialAidCorrectionFieldSelection = (
  fields: FinancialAidCorrectionField[],
  visibleFields: FinancialAidCorrectionField[],
  currentPath: string,
): FinancialAidCorrectionFieldSelection | null => {
  const visiblePathSet = new Set(visibleFields.map((field) => field.path));
  const currentField = fields.find((field) => field.path === currentPath);
  if (currentField && visiblePathSet.has(currentField.path)) {
    return {
      path: currentField.path,
      inputValue: currentField.inputValue,
    };
  }
  const firstVisibleField = visibleFields[0] ?? null;
  return firstVisibleField
    ? {
      path: firstVisibleField.path,
      inputValue: firstVisibleField.inputValue,
    }
    : null;
};

export const mergeFinancialAidCorrectionSections = (
  sections: unknown,
  draftSections: unknown,
): unknown => {
  if (Array.isArray(draftSections)) {
    const sectionArray = Array.isArray(sections) ? sections : [];
    const length = Math.max(sectionArray.length, draftSections.length);
    return Array.from({ length }, (_, index) => {
      if (!(index in draftSections) || draftSections[index] === undefined) {
        return sectionArray[index];
      }
      return mergeFinancialAidCorrectionSections(sectionArray[index], draftSections[index]);
    });
  }
  if (!isRecord(draftSections)) {
    return sections;
  }
  if (!isRecord(sections)) {
    return { ...draftSections };
  }
  const merged: Record<string, unknown> = { ...sections };
  Object.entries(draftSections).forEach(([key, draftValue]) => {
    const currentValue = sections[key];
    merged[key] = (isRecord(currentValue) && isRecord(draftValue)) || Array.isArray(draftValue)
      ? mergeFinancialAidCorrectionSections(currentValue, draftValue)
      : draftValue;
  });
  return merged;
};

const collectChangedCorrectionPaths = (
  before: unknown,
  after: unknown,
  path: string,
): string[] => {
  if (Object.is(before, after)) {
    return [];
  }
  if (Array.isArray(before) || Array.isArray(after)) {
    const beforeArray = Array.isArray(before) ? before : [];
    const afterArray = Array.isArray(after) ? after : [];
    const length = Math.max(beforeArray.length, afterArray.length);
    return Array.from({ length }).flatMap((_, index) =>
      collectChangedCorrectionPaths(beforeArray[index], afterArray[index], `${path}.${index}`)
    );
  }
  if (isRecord(before) || isRecord(after)) {
    const beforeRecord = isRecord(before) ? before : {};
    const afterRecord = isRecord(after) ? after : {};
    const keys = new Set([...Object.keys(beforeRecord), ...Object.keys(afterRecord)]);
    return Array.from(keys).flatMap((key) =>
      collectChangedCorrectionPaths(
        beforeRecord[key],
        afterRecord[key],
        path ? `${path}.${key}` : key,
      )
    );
  }
  return path && !isFinancialAidCorrectionProtectedPath(path) ? [path] : [];
};

export const listFinancialAidCorrectionDraftPaths = (
  sections: unknown,
  draftSections: unknown,
): string[] => {
  const mergedSections = mergeFinancialAidCorrectionSections(sections, draftSections);
  return collectChangedCorrectionPaths(sections, mergedSections, '')
    .filter((path, index, paths) => Boolean(path) && paths.indexOf(path) === index)
    .sort((left, right) => left.localeCompare(right));
};

export const readFinancialAidCorrectionPathValue = (sections: unknown, path: string): unknown => {
  const segments = path.split('.').filter(Boolean);
  let current = sections;
  for (const segment of segments) {
    if (Array.isArray(current) && /^\d+$/.test(segment)) {
      current = current[Number(segment)];
      continue;
    }
    if (!isRecord(current)) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
};

const parseCorrectionInputValue = (path: string, currentValue: unknown, rawValue: string): unknown => {
  if (typeof currentValue === 'number') {
    const next = normalizeNumberInput(rawValue);
    if (next === null) {
      throw new Error('Corrected value must be a finite number.');
    }
    return next;
  }
  if (isKnownParentSnapshotNumericPath(path)) {
    const next = normalizeNumberInput(rawValue);
    if (next === null) {
      throw new Error('Corrected value must be a finite number.');
    }
    return rawValue.trim();
  }
  if (typeof currentValue === 'boolean' || isKnownParentSnapshotBooleanPath(path)) {
    if (rawValue !== 'true' && rawValue !== 'false') {
      throw new Error('Corrected boolean value must be true or false.');
    }
    return rawValue === 'true';
  }
  assertValidParentContactCorrectionValue(path, rawValue);
  if (shouldTrimParentContactCorrectionValue(path)) {
    return rawValue.trim();
  }
  if (currentValue == null) {
    return rawValue.trim() ? rawValue : null;
  }
  return rawValue;
};

const cloneContainer = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.slice();
  }
  if (isRecord(value)) {
    return { ...value };
  }
  return {};
};

const setNestedValue = (container: unknown, segments: string[], value: unknown): unknown => {
  if (segments.length === 0) {
    return value;
  }
  const [head, ...tail] = segments;
  const next = cloneContainer(container);
  if (Array.isArray(next) && /^\d+$/.test(head)) {
    const index = Number(head);
    next[index] = setNestedValue(next[index], tail, value);
    return next;
  }
  const record = next as Record<string, unknown>;
  record[head] = setNestedValue(record[head], tail, value);
  return record;
};

const buildParentSnapshotSparsePatch = (
  currentValue: unknown,
  segments: string[],
  value: unknown,
): unknown => {
  if (segments.length === 0) {
    return value;
  }
  const [head, ...tail] = segments;
  if (/^\d+$/.test(head)) {
    const index = Number(head);
    const next: unknown[] = Array.from({ length: index + 1 }, () => ({}));
    next[index] = setNestedValue({}, tail, value);
    return next;
  }
  const currentRecord = isRecord(currentValue) ? currentValue : {};
  return {
    [head]: buildParentSnapshotSparsePatch(currentRecord[head], tail, value),
  };
};

const removeNestedDraftValue = (value: unknown, segments: string[]): unknown => {
  if (segments.length === 0) {
    return undefined;
  }
  if (!isRecord(value) && !Array.isArray(value)) {
    return value;
  }
  const [head, ...tail] = segments;
  const next = Array.isArray(value) ? value.slice() : { ...value };
  if (tail.length === 0) {
    if (Array.isArray(next) && /^\d+$/.test(head)) {
      delete next[Number(head)];
    } else {
      delete (next as Record<string, unknown>)[head];
    }
    return next;
  }
  const child = Array.isArray(value) && /^\d+$/.test(head)
    ? value[Number(head)]
    : (value as Record<string, unknown>)[head];
  const removedChild = removeNestedDraftValue(child, tail);
  if (removedChild === undefined) {
    if (Array.isArray(next) && /^\d+$/.test(head)) {
      delete next[Number(head)];
    } else {
      delete (next as Record<string, unknown>)[head];
    }
  } else if (Array.isArray(next) && /^\d+$/.test(head)) {
    next[Number(head)] = removedChild;
  } else {
    (next as Record<string, unknown>)[head] = removedChild;
  }
  return next;
};

const isEmptyCorrectionDraftValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.every((item) => item === undefined || isEmptyCorrectionDraftValue(item));
  }
  if (isRecord(value)) {
    return Object.values(value).every(isEmptyCorrectionDraftValue);
  }
  return value === undefined;
};

const pruneUnchangedCorrectionDraftValue = (originalValue: unknown, draftValue: unknown): unknown => {
  if (draftValue === undefined) {
    return undefined;
  }
  if (Array.isArray(draftValue)) {
    const originalArray = Array.isArray(originalValue) ? originalValue : [];
    const next: unknown[] = [];
    draftValue.forEach((item, index) => {
      if (!(index in draftValue)) {
        return;
      }
      const pruned = pruneUnchangedCorrectionDraftValue(originalArray[index], item);
      if (pruned !== undefined && !isEmptyCorrectionDraftValue(pruned)) {
        next[index] = pruned;
      }
    });
    return isEmptyCorrectionDraftValue(next) ? undefined : next;
  }
  if (isRecord(draftValue)) {
    const originalRecord = isRecord(originalValue) ? originalValue : {};
    const next: Record<string, unknown> = {};
    Object.entries(draftValue).forEach(([key, item]) => {
      const pruned = pruneUnchangedCorrectionDraftValue(originalRecord[key], item);
      if (pruned !== undefined && !isEmptyCorrectionDraftValue(pruned)) {
        next[key] = pruned;
      }
    });
    return Object.keys(next).length > 0 ? next : undefined;
  }
  if (originalValue === undefined && (draftValue === null || draftValue === '')) {
    return undefined;
  }
  return Object.is(originalValue, draftValue) ? undefined : draftValue;
};

export const pruneFinancialAidCorrectionDraftSections = (
  sections: unknown,
  draftSections: unknown,
): Record<string, unknown> => {
  const prunedDraftSections = pruneUnchangedCorrectionDraftValue(sections, draftSections);
  return isRecord(prunedDraftSections) ? prunedDraftSections : {};
};

export const removeFinancialAidCorrectionDraftPath = (
  sections: unknown,
  draftSections: unknown,
  path: string,
): Record<string, unknown> => {
  const removedDraftSections = removeNestedDraftValue(draftSections, path.split('.').filter(Boolean));
  return pruneFinancialAidCorrectionDraftSections(sections, removedDraftSections);
};

export const buildFinancialAidCorrectionPatch = (
  sections: unknown,
  path: string,
  rawValue: string,
): FinancialAidCorrectionPatch => {
  if (isFinancialAidCorrectionProtectedPath(path)) {
    throw new Error('This financial aid field is protected.');
  }
  const [sectionKey, ...rest] = path.split('.').filter(Boolean);
  if (!sectionKey || rest.length === 0 || !isRecord(sections)) {
    throw new Error('Choose a submitted financial aid field to correct.');
  }
  if (isParentSnapshotCorrectionPath(path) && !isKnownParentSnapshotCorrectionLeafPath(path)) {
    throw new Error('Choose a submitted financial aid field to correct.');
  }

  const sectionValue = sections[sectionKey];
  const currentValue = readFinancialAidCorrectionPathValue(sections, path);
  const parsedValue = parseCorrectionInputValue(path, currentValue, rawValue);
  if (isParentSnapshotCorrectionPath(path)) {
    const snapshotPath = path.split('.').slice(PARENT_SNAPSHOT_PREFIX_SEGMENTS.length);
    return {
      sectionKey,
      patch: {
        parentCollectedFinancialAidSnapshot: buildParentSnapshotSparsePatch(
          readFinancialAidCorrectionPathValue(sections, PARENT_SNAPSHOT_PREFIX),
          snapshotPath,
          parsedValue,
        ),
      },
    };
  }
  if (Array.isArray(sectionValue)) {
    return {
      sectionKey,
      patch: setNestedValue(sectionValue, rest, parsedValue),
    };
  }

  const [topKey, ...nestedPath] = rest;
  if (nestedPath.length === 0) {
    return {
      sectionKey,
      patch: { [topKey]: parsedValue },
    };
  }
  const sectionRecord = isRecord(sectionValue) ? sectionValue : {};
  return {
    sectionKey,
    patch: {
      [topKey]: setNestedValue(sectionRecord[topKey], nestedPath, parsedValue),
    },
  };
};
